import { TextField, type TextFieldProps, Typography } from '../primitives/index.js'
import { graphHooks } from '@tunasong/graph-lib/react'
import type { Entity, Persisted } from '@tunasong/schemas'
import type { FC } from 'react'
import React, { useEffect } from 'react'
import { focusNextElement } from '../dom/focus.js'
import { VBox } from '../layout/vertical-box.js'

export interface EntityTitleProps extends Omit<TextFieldProps, 'ref'> {
  entity?: Persisted<Entity>
  /** Horizontal alignment of the title and its subtitle. @default 'center' */
  align?: 'center' | 'start'
}

export const EntityTitle: FC<EntityTitleProps> = props => {
  const { entity, style, align = 'center', ...inputProps } = props
  const updateEntity = graphHooks.useEntityUpdate({ debounceDelay: 1000 })
  const inputRef = React.useRef<HTMLInputElement>(null)

  const hasEdited = React.useRef(false)

  const handleTitle: React.ChangeEventHandler<HTMLInputElement> = e => {
    if (!entity) {
      return
    }
    hasEdited.current = true
    updateEntity(entity.id, { name: e.target.value })
  }
  const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      e.stopPropagation()
      if (inputRef.current) {
        focusNextElement(inputRef.current)
      }
    }
  }

  // Update the input value if the entity name changes externally (e.g., rename dialog)
  useEffect(() => {
    if (!inputRef.current) {
      return
    }
    // Skip if the user is actively editing (input is focused)
    if (document.activeElement === inputRef.current) {
      return
    }
    inputRef.current.value = entity?.name ?? ''
    hasEdited.current = false
  }, [entity?.name])

  if (!entity) {
    return null
  }

  return (
    <VBox data-entity-title style={{ width: '100%', textAlign: align }}>
      <TextField
        key={entity.id}
        variant="standard"
        style={{
          fontSize: 36,
          fontWeight: 300,
          textAlign: align,
          width: '100%',
          margin: 0,
          ...style,
        }}
        placeholder={'Untitled'}
        aria-label="Document title"
        ref={inputRef}
        defaultValue={entity.name ?? ''}
        autoFocus={!entity.name}
        autoCorrect="off"
        autoComplete="off"
        onKeyDown={handleKeyDown}
        onChange={handleTitle}
        {...inputProps}
      />
      {entity.description ? <Typography variant="caption">{entity.description}</Typography> : null}
    </VBox>
  )
}

export default EntityTitle
